import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';

const CustomFields = () => {
  const [customFields, setCustomFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [formData, setFormData] = useState({
    field_name: '',
    field_type: 'text',
    field_options: '',
    is_active: true
  });
  const [userPermissions, setUserPermissions] = useState({
    can_view: false,
    can_add: false,
    can_edit: false,
    can_delete: false,
    can_export: false,
    can_import: false,
    loaded: false
  });

  useAuth();
  const navigate = useNavigate();
  const API_BASE = process.env.REACT_APP_API_BASE;

  const fetchUserPermissions = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setUserPermissions({
          can_view: false,
          can_add: false,
          can_edit: false,
          can_delete: false,
          can_export: false,
          can_import: false,
          loaded: true
        });
        return;
      }

      const response = await axios.get(`${API_BASE}/get_user_permissions.php`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success && response.data.permissions) {
        // Find permissions for Custom Fields module
        const customFieldsPermissions = response.data.permissions.find(
          p => p.module_name === 'Custom Fields' || p.module_name === 'CustomFields'
        );

        if (customFieldsPermissions) {
          setUserPermissions({
            can_view: Boolean(customFieldsPermissions.can_view),
            can_add: Boolean(customFieldsPermissions.can_add),
            can_edit: Boolean(customFieldsPermissions.can_edit),
            can_delete: Boolean(customFieldsPermissions.can_delete),
            can_export: Boolean(customFieldsPermissions.can_export),
            can_import: Boolean(customFieldsPermissions.can_import || false), // Handle can_import
            loaded: true
          });
        } else {
          // If no custom fields permissions found, set all to false
          setUserPermissions({
            can_view: false,
            can_add: false,
            can_edit: false,
            can_delete: false,
            can_export: false,
            can_import: false,
            loaded: true
          });
        }
      } else {
        setUserPermissions({
          can_view: false,
          can_add: false,
          can_edit: false,
          can_delete: false,
          can_export: false,
          can_import: false,
          loaded: true
        });
      }
    } catch (error) {
      console.error('Error fetching permissions:', error);
      setUserPermissions({
        can_view: false,
        can_add: false,
        can_edit: false,
        can_delete: false,
        can_export: false,
        can_import: false,
        loaded: true
      });
    }
  }, [API_BASE]);

  const fetchCustomFields = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_BASE}/get_custom_fields.php`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setCustomFields(response.data.fields);
      }
    } catch (error) {
      setError('Failed to fetch custom fields');
      console.error('Error fetching custom fields:', error);
    } finally {
      setLoading(false);
    }
  }, [API_BASE]);

  useEffect(() => {
    const initializeData = async () => {
      await fetchUserPermissions();
      if (userPermissions.can_view) {
        await fetchCustomFields();
      }
    };

    initializeData();
  }, [fetchUserPermissions, fetchCustomFields, userPermissions.can_view]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (editingField && !userPermissions.can_edit) {
      alert('You do not have permission to edit custom fields');
      return;
    }

    if (!editingField && !userPermissions.can_add) {
      alert('You do not have permission to add custom fields');
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const url = editingField
        ? `${API_BASE}/update_custom_field.php`
        : `${API_BASE}/add_custom_field.php`;

      const response = await axios.post(url,
        editingField ? { ...formData, field_id: editingField.field_id } : formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        fetchCustomFields();
        resetForm();
        setShowModal(false);
      } else {
        setError(response.data.error || 'Failed to save custom field');
      }
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to save custom field');
    }
  };

  const handleEdit = (field) => {
    if (!userPermissions.can_edit) {
      alert('You do not have permission to edit custom fields');
      return;
    }
    setEditingField(field);
    setFormData({
      field_name: field.field_name,
      field_type: field.field_type,
      field_options: field.field_options || '',
      is_active: field.is_active
    });
    setShowModal(true);
  };

  const handleDelete = async (fieldId) => {
    if (!userPermissions.can_delete) {
      alert('You do not have permission to delete custom fields');
      return;
    }

    if (window.confirm('Are you sure you want to delete this custom field? This will also delete all associated values for employees.')) {
      try {
        const token = localStorage.getItem('authToken');
        const response = await axios.post(`${API_BASE}/delete_custom_field.php`,
          { field_id: fieldId },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data.success) {
          fetchCustomFields();
        } else {
          setError(response.data.error || 'Failed to delete custom field');
        }
      } catch (error) {
        setError(error.response?.data?.error || 'Failed to delete custom field');
      }
    }
  };

  const handleToggleStatus = async (field) => {
    if (!userPermissions.can_edit) {
      alert('You do not have permission to update custom fields');
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.post(`${API_BASE}/update_custom_field.php`,
        {
          field_id: field.field_id,
          field_name: field.field_name,
          field_type: field.field_type,
          field_options: field.field_options,
          is_active: field.is_active ? 0 : 1
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        fetchCustomFields();
      }
    } catch (error) {
      setError('Failed to update field status');
    }
  };

  // Import functionality (example - you can customize this)
  const handleImport = () => {
    if (!userPermissions.can_import) {
      alert('You do not have permission to import custom fields');
      return;
    }

    // You can implement import logic here
    // For example, show an import modal or trigger file input
    alert('Import functionality would be implemented here');

    // Example implementation:
    // const fileInput = document.createElement('input');
    // fileInput.type = 'file';
    // fileInput.accept = '.csv,.json,.xlsx';
    // fileInput.onchange = handleFileUpload;
    // fileInput.click();
  };

  // Export functionality (example - you can customize this)
  const handleExport = () => {
    if (!userPermissions.can_export) {
      alert('You do not have permission to export custom fields');
      return;
    }

    // You can implement export logic here
    // For example, export to CSV or Excel
    alert('Export functionality would be implemented here');

    // Example implementation:
    // const csvContent = convertToCSV(customFields);
    // downloadCSV(csvContent, 'custom_fields.csv');
  };

  const resetForm = () => {
    setFormData({
      field_name: '',
      field_type: 'text',
      field_options: '',
      is_active: true
    });
    setEditingField(null);
  };

  const handleModalClose = () => {
    setShowModal(false);
    resetForm();
  };

  // Navigation handlers
  const goToDashboard = () => {
    navigate('/dashboard');
  };

  const goToEmployees = () => {
    navigate('/employees');
  };

  const goToHome = () => {
    navigate('/');
  };

  // Permission check functions
  const canViewCustomFields = () => userPermissions.can_view;
  const canAddCustomField = () => userPermissions.can_add;
  const canEditCustomField = () => userPermissions.can_edit;
  const canDeleteCustomField = () => userPermissions.can_delete;
  const canExportCustomFields = () => userPermissions.can_export;
  const canImportCustomFields = () => userPermissions.can_import;

  // Show loading while permissions are being fetched
  if (loading || !userPermissions.loaded) {
    return (
      <div className="d-flex justify-content-center align-items-center youtube-page-container" style={{ height: '50vh' }}>
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2 text-muted">Loading permissions...</p>
        </div>
      </div>
    );
  }

  // If user doesn't have view permission, show access denied
  if (!canViewCustomFields()) {
    return (
      <div className="container-fluid py-4 youtube-page-container">
        <div className="row">
          <div className="col-12">
            <div className="card shadow-sm border-0">
              <div className="card-body text-center py-5">
                <i className="bi bi-shield-exclamation display-1 text-warning"></i>
                <h3 className="mt-3 text-warning">Access Denied</h3>
                <p className="text-muted">You do not have permission to view custom fields.</p>
                <button
                  className="btn btn-primary mt-3"
                  onClick={goToDashboard}
                >
                  <i className="bi bi-speedometer2 me-2"></i>
                  Back to Dashboard
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4 youtube-page-container">
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card shadow-sm border-0">
            <div className="card-body py-4">
              <div className="row align-items-center">
                <div className="col-md-6">
                  <div className="d-flex align-items-center">
                    <div className="btn-group me-3" role="group">
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={goToHome}
                        title="Go to Home"
                      >
                        <i className="bi bi-house"></i>
                      </button>
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={goToDashboard}
                        title="Go to Dashboard"
                      >
                        <i className="bi bi-speedometer2"></i>
                      </button>
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={goToEmployees}
                        title="Go to Employees"
                      >
                        <i className="bi bi-people"></i>
                      </button>
                    </div>
                    <div>
                      <h2 className="h4 mb-1 fw-bold text-dark">
                        <i className="bi bi-list-check me-2 text-primary"></i>
                        Custom Fields Management
                        {!canAddCustomField() && !canEditCustomField() && !canDeleteCustomField() && (
                          <span className="badge bg-warning ms-2">View Only</span>
                        )}
                      </h2>
                      <p className="text-muted mb-0">
                        Manage additional employee information fields
                      </p>
                    </div>
                  </div>
                </div>
                <div className="col-md-6 text-end">
                  <div className="btn-group" role="group">
                    {canImportCustomFields() && (
                      <button
                        className="btn btn-outline-success btn-lg"
                        onClick={handleImport}
                        title="Import Custom Fields"
                      >
                        <i className="bi bi-upload me-2"></i>
                        Import
                      </button>
                    )}
                    {canExportCustomFields() && (
                      <button
                        className="btn btn-outline-info btn-lg"
                        onClick={handleExport}
                        title="Export Custom Fields"
                      >
                        <i className="bi bi-download me-2"></i>
                        Export
                      </button>
                    )}
                    {canAddCustomField() && (
                      <button
                        className="btn btn-primary btn-lg"
                        onClick={() => setShowModal(true)}
                      >
                        <i className="bi bi-plus-circle me-2"></i>
                        Add Custom Field
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Navigation */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card bg-light border-0">
            <div className="card-body py-3">
              <div className="row text-center">
                <div className="col-md-3 mb-2 mb-md-0">
                  <button
                    className="btn btn-outline-primary w-100 d-flex align-items-center justify-content-center"
                    onClick={goToHome}
                  >
                    <i className="bi bi-house me-2"></i>
                    Home
                  </button>
                </div>
                <div className="col-md-3 mb-2 mb-md-0">
                  <button
                    className="btn btn-outline-success w-100 d-flex align-items-center justify-content-center"
                    onClick={goToDashboard}
                  >
                    <i className="bi bi-speedometer2 me-2"></i>
                    Dashboard
                  </button>
                </div>
                <div className="col-md-3 mb-2 mb-md-0">
                  <button
                    className="btn btn-primary w-100 d-flex align-items-center justify-content-center"
                    onClick={goToEmployees}
                  >
                    <i className="bi bi-people me-2"></i>
                    Employees
                  </button>
                </div>
                <div className="col-md-3">
                  <Link
                    to="/menu"
                    className="btn btn-outline-info w-100 d-flex align-items-center justify-content-center"
                  >
                    <i className="bi bi-grid-3x3-gap me-2"></i>
                    Menu
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="alert alert-danger d-flex align-items-center" role="alert">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              <div>{error}</div>
              <button
                type="button"
                className="btn-close ms-auto"
                onClick={() => setError('')}
              ></button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Fields Table */}
      <div className="row">
        <div className="col-12">
          <div className="card shadow-sm border-0">
            <div className="card-body p-0">
              {customFields.length === 0 ? (
                <div className="text-center py-5">
                  <i className="bi bi-list-check display-1 text-muted"></i>
                  <h4 className="mt-3 text-muted">No custom fields found</h4>
                  <p className="text-muted">
                    Get started by creating your first custom field to collect additional employee information.
                  </p>
                  <div className="mt-3">
                    {canImportCustomFields() && (
                      <button
                        className="btn btn-success me-2"
                        onClick={handleImport}
                      >
                        <i className="bi bi-upload me-2"></i>
                        Import Fields
                      </button>
                    )}
                    {canAddCustomField() && (
                      <button
                        className="btn btn-primary"
                        onClick={() => setShowModal(true)}
                      >
                        <i className="bi bi-plus-circle me-2"></i>
                        Create First Field
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th className="ps-4">Field Name</th>
                        <th>Field Type</th>
                        <th>Options</th>
                        <th>Status</th>
                        <th className="text-end pe-4">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {customFields.map((field) => (
                        <tr key={field.field_id}>
                          <td className="ps-4">
                            <div className="fw-semibold">{field.field_name}</div>
                            <small className="text-muted">ID: {field.field_id}</small>
                          </td>
                          <td>
                            <span className={`badge ${field.field_type === 'text' ? 'bg-primary' :
                              field.field_type === 'number' ? 'bg-info' :
                                field.field_type === 'date' ? 'bg-success' :
                                  field.field_type === 'boolean' ? 'bg-warning' : 'bg-secondary'
                              }`}>
                              {field.field_type}
                            </span>
                          </td>
                          <td>
                            <small className="text-muted">
                              {field.field_options || 'N/A'}
                            </small>
                          </td>
                          <td>
                            <div className="form-check form-switch">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                checked={field.is_active}
                                onChange={() => handleToggleStatus(field)}
                                disabled={!canEditCustomField()}
                              />
                              <label className="form-check-label">
                                {field.is_active ? 'Active' : 'Inactive'}
                              </label>
                            </div>
                          </td>
                          <td className="text-end pe-4">
                            <div className="btn-group" role="group">
                              {canEditCustomField() && (
                                <button
                                  className="btn btn-outline-primary btn-sm"
                                  onClick={() => handleEdit(field)}
                                  title="Edit"
                                >
                                  <i className="bi bi-pencil"></i>
                                </button>
                              )}
                              {canDeleteCustomField() && (
                                <button
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={() => handleDelete(field.field_id)}
                                  title="Delete"
                                >
                                  <i className="bi bi-trash"></i>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Stats Footer */}
      <div className="row mt-4">
        <div className="col-12">
          <div className="card bg-light border-0">
            <div className="card-body py-3">
              <div className="row text-center">
                <div className="col-md-3">
                  <div className="fw-bold text-primary fs-4">{customFields.length}</div>
                  <small className="text-muted">Total Fields</small>
                </div>
                <div className="col-md-3">
                  <div className="fw-bold text-success fs-4">
                    {customFields.filter(f => f.is_active).length}
                  </div>
                  <small className="text-muted">Active Fields</small>
                </div>
                <div className="col-md-3">
                  <div className="fw-bold text-warning fs-4">
                    {customFields.filter(f => !f.is_active).length}
                  </div>
                  <small className="text-muted">Inactive Fields</small>
                </div>
                <div className="col-md-3">
                  <div className="fw-bold text-info fs-4">
                    {userPermissions.can_import ? 'Yes' : 'No'}
                  </div>
                  <small className="text-muted">Import Allowed</small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Navigation Footer */}
      <div className="row mt-4">
        <div className="col-12">
          <div className="card border-0">
            <div className="card-body text-center py-3">
              <div className="btn-group" role="group">
                <button
                  className="btn btn-outline-primary"
                  onClick={goToHome}
                >
                  <i className="bi bi-house me-2"></i>
                  Back to Home
                </button>
                <button
                  className="btn btn-outline-success"
                  onClick={goToDashboard}
                >
                  <i className="bi bi-speedometer2 me-2"></i>
                  Back to Dashboard
                </button>
                <button
                  className="btn btn-outline-info"
                  onClick={goToEmployees}
                >
                  <i className="bi bi-people me-2"></i>
                  Back to Employees
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow">
              <div className="modal-header bg-primary text-white">
                <h5 className="modal-title">
                  <i className="bi bi-list-check me-2"></i>
                  {editingField ? 'Edit Custom Field' : 'Add Custom Field'}
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={handleModalClose}
                ></button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="row g-3">
                    <div className="col-12">
                      <label className="form-label">Field Name *</label>
                      <input
                        type="text"
                        className="form-control"
                        name="field_name"
                        value={formData.field_name}
                        onChange={handleInputChange}
                        required
                        placeholder="Enter field name (e.g., Emergency Contact, Blood Group)"
                      />
                    </div>

                    <div className="col-12">
                      <label className="form-label">Field Type *</label>
                      <select
                        className="form-select"
                        name="field_type"
                        value={formData.field_type}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="text">Text</option>
                        <option value="number">Number</option>
                        <option value="date">Date</option>
                        <option value="boolean">Yes/No</option>
                        <option value="select">Dropdown</option>
                      </select>
                    </div>

                    {formData.field_type === 'select' && (
                      <div className="col-12">
                        <label className="form-label">Options (comma-separated) *</label>
                        <input
                          type="text"
                          className="form-control"
                          name="field_options"
                          value={formData.field_options}
                          onChange={handleInputChange}
                          required={formData.field_type === 'select'}
                          placeholder="Option 1, Option 2, Option 3"
                        />
                        <div className="form-text">
                          Enter options separated by commas. For example: "Full Time, Part Time, Contract"
                        </div>
                      </div>
                    )}

                    <div className="col-12">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          name="is_active"
                          checked={formData.is_active}
                          onChange={handleInputChange}
                        />
                        <label className="form-check-label">
                          Active Field
                        </label>
                      </div>
                      <div className="form-text">
                        Inactive fields won't be shown when adding/editing employees
                      </div>
                    </div>
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleModalClose}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                  >
                    {editingField ? 'Update Field' : 'Add Field'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomFields;